import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';

import { LanguageService } from '../language/language.service';

import { environment } from '../../../environments/environment';

// Popups
import { MatDialog } from '@angular/material/dialog';
import { CookiesPopupComponent } from '../../components/popups/cookies.popup/cookies.popup.component';

// Meta configuration for SEO
import { Meta, Title, MetaDefinition } from '@angular/platform-browser';


@Injectable({
  providedIn: 'root'
})
export class ControllerService {
  /* 
  Se han marcado "currentRouteSubject", "Router" y los servicios "Title" 
  y "Meta" como "readonly", porque se inyectan en el constructor y no se 
  vuelven a modificar. Esto es una buena práctica.
  */
  // Observable for the current route
  private readonly currentRouteSubject = new BehaviorSubject<string>('');


  constructor(
    public languageService: LanguageService,
    private readonly router: Router,
    public dialog: MatDialog,
    private readonly titleService: Title, 
    private readonly metaService: Meta
  ){}
  
  initializeApp(){
    // Set the default language
    this.languageService.setLanguageAccordingToNavigatorLanguage()
    // Set SEO meta tags
    this.setMetaInformationForSEO()
    // Ask for user consent 
    this.askForConsent()
    // Initialize the firebase analytics after getting user consent
    if(this.getConsent()){
      this.loadGtag()
    }
    // Subscribe to routes
    this.subscribeToRouteChanges();
  }


  // ------------ Router -------------//
  navigate(route:string){
    this.router.navigate([route]);
  }

  getCurrentRoute(): Observable<string> {
    return this.currentRouteSubject.asObservable();
  }

  subscribeToRouteChanges() {
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd)
    ).subscribe(() => {
      const currentRoute = this.router.url; 
      this.currentRouteSubject.next(currentRoute);
    });
  }


  // SEO Configuration
  setMetaInformationForSEO(){
    // Get values according to language code
    let values:any = {}
    if(this.languageService.languageCode === 'es') values = environment.metaInformation.es
    else values = environment.metaInformation.en
    // Page title
    this.titleService.setTitle(values['og:title'])
    for (const key in values) {
      // Skip 'og:title' since it has already been set
      if(key === 'og:title') continue
      // Set the corresponding tag selector ('name' or 'property')
      const tagSelector = key.startsWith('og:') ? `property='${key}'` : `name='${key}'`;
      // Verify if the tag selector exists
      let tagExists = !!this.metaService.getTag(tagSelector);
      // Create the meta tag
      const tag: MetaDefinition = {
        content: values[key],
      };
      if (key.startsWith('og:')) tag.property = key;
      else tag.name = key;
      // Actualizar o agregar el meta tag
      if (tagExists) {
        this.metaService.updateTag(tag, tagSelector);
      } else {
        this.metaService.addTag(tag);
      }
    }
    // ---- //
    // Add or update robots meta tag depending on environment (avoid indexing the test environment)
    const robotsTagSelector = `name='robots'`;
    const isProduction = environment.production;
    const robotsMetaTag: MetaDefinition = {
      name: 'robots',
      content: isProduction ? 'index, follow' : 'noindex, nofollow',
    };
    // Verify if the tag already exists
    let robotsTagExists = !!this.metaService.getTag(robotsTagSelector);
    if (robotsTagExists) {
      this.metaService.updateTag(robotsMetaTag, robotsTagSelector);
    } else {
      this.metaService.addTag(robotsMetaTag);
    }
  }



  // Firebase analytics
  loadGtag() {
    const measurementId = environment.firebaseConfig.measurementId;
    // Cargar el script de gtag.js
    const script = document.createElement('script');
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    script.async = true;
    document.head.appendChild(script);
    // Inicializar gtag
    window.dataLayer = window.dataLayer || [];
    window.gtag = function () { window.dataLayer.push(arguments); };
    window.gtag('js', new Date());
    window.gtag('config', measurementId);
  }

  

  // Cookies
  askForConsent(){
    if(!this.getConsent()){
      this.openCookiesPopup()
    }
  }
  getConsent(): boolean {
    return JSON.parse(localStorage.getItem('firebaseAnalyticsConsent') ?? 'false');
  }
  setConsent(consent: boolean) {
    localStorage.setItem('firebaseAnalyticsConsent', JSON.stringify(consent));
  }
  openCookiesPopup(){
    // Open the popup
    this.dialog.open(
      CookiesPopupComponent, 
      {
        disableClose: true,
        /*data: {},
        width: '100vw',   
        maxWidth: '100vw', 
        height: '100vh',*/
      } 
    ).afterClosed().subscribe(result => this.setConsent(result))
  }


}
